import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  addCaseNotesAttachments,
  addUploadRecordFiles,
  deleteUploadRecordFile,
  updateUploadRecordFiles,
  uploadChunkRecord,
  uploadChunkRecordFinal,
  uploadPageCount,
} from "../services/UploadRecordsApi";
import { v4 as uuidv4 } from "uuid";
import Snackbar from "./Snackbar";
import Loader from "./Loader";
import MultilineTextBox from "./MultilineTextBox";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/Store";
import PdfViewerDrawer from "./PDFFileViewerDrawer";
import { FILE_SIZE } from "./Constant";
import axios, { CancelToken } from "axios";
import UploadStatusFloatingIcon from "./UploadStatusFloatingIcon";
import { setIsUploadFile } from "../redux/slice/CaseHeaderSlice";

const MAX_FILE_SIZE_MB = 2048;
const MAX_TOTAL_SIZE_MB = 2048;

interface FileData {
  id?: any;
  file?: File;
  fileName: string;
  description: string;
  cloudStoragePath?: string;
  isEditing: boolean;
}

interface EditableFileTableProps {
  caseInfoId: any;
  comments?: any;
  initialData?: FileData[];
  onAdd?: (fileData: FileData) => void;
  onEdit?: (id: string, fileData: FileData) => void;
  onDelete?: (id: string) => void;
  onFileIdsUpdate?: (ids: string[], isSaveDisabled?: boolean) => void;
  result: any;
  isCancel: boolean;
  documentType: string | null | number;
  isRowActionTriggered: boolean;
  isdisable?: any;
}

interface UploadStatus {
  fileName?: string;
  progress: number;
  isUploading: boolean;
  cancel?: () => void;
}

const ALLOWED_FILE_TYPES = [
  // Document formats
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",

  // Image formats
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/tiff",
  "image/heif",
  "image/heic",
  "image/webp",

  // Video formats
  "video/mp4",
  "video/x-msvideo",
  "video/quicktime",
  "video/x-ms-wmv",
  "video/x-matroska",
  "video/webm",
  "video/x-flv",
  "video/3gpp",
  "video/mpeg",
  "video/avchd",

  // Audio formats
  "audio/m4a",
  "audio/flac",
  "audio/mpeg",
  "audio/mp4",
  "audio/wav",
  "audio/x-wav",
  "audio/x-ms-wma",
  "audio/aac",
  "audio/alac",
  "audio/aiff",
  "audio/x-aiff",

  // Archive formats
  "application/zip",
  "application/x-zip-compressed",
];

const ALLOWED_FILE_EXTENSIONS = [
  // Document extensions
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".txt",
  ".csv",

  // Image extensions
  ".png",
  ".jpeg",
  ".jpg",
  ".gif",
  ".tiff",
  ".raw",
  ".heif",
  ".heic",
  ".webp",

  // Video extensions
  ".mp4",
  ".avi",
  ".mov",
  ".wmv",
  ".mkv",
  ".webm",
  ".flv",
  ".3gp",
  ".mpeg",
  ".avchd",

  // Audio extensions
  ".m4a",
  ".flac",
  ".mp3",
  ".mp4",
  ".wav",
  ".wma",
  ".aac",
  ".alac",
  ".aiff",

  // Archive extensions
  ".zip",
];

function isAllowedFileType(file: File): boolean {
  const mimeTypeAllowed = ALLOWED_FILE_TYPES.includes(file.type);
  const extensionAllowed = ALLOWED_FILE_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );
  return mimeTypeAllowed || extensionAllowed;
}

const EditableFileTable: React.FC<EditableFileTableProps> = ({
  caseInfoId,
  comments,
  initialData = [],
  onAdd,
  onEdit,
  onDelete,
  onFileIdsUpdate,
  result,
  isCancel,
  documentType,
  isRowActionTriggered,
  isdisable,
}) => {
  const { view_Case } = useSelector((state: RootState) => state.featureAccess);
  const [files, setFiles] = useState<FileData[]>(initialData);
  const [originalData, setOriginalData] = useState<{ [key: string]: FileData }>(
    {}
  );
  // const [newFile, setNewFile] = useState<FileData>({
  //   id: "",
  //   fileName: "",
  //   description: "",
  //   isEditing: true,
  // });
  const [newFile, setNewFile] = useState<{
    files: File[];
    description: string;
  }>({ files: [], description: "" });
  const [newVersionFile, setNewVersionFile] = useState<FileData>({
    id: "",
    fileName: "",
    description: "",
    isEditing: true,
    cloudStoragePath: "",
  });
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingNew, setLoadingNew] = useState<boolean>(false);
  const [isSaveBtnDisable, setIsSaveBtnDisable] = useState<boolean>(false);
  const [isSaveBtnDisableForRow, setIsSaveBtnDisableForRow] =
    useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showModalNew, setShowModalNew] = useState(false);
  const [fileToCancel, setFileToCancel] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileNewVersionInputRef = useRef<HTMLInputElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const editFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {}
  );
  const [fileToDelete, setFileToDelete] = useState<{
    id: number;
    fileName: string;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const prevDocumentTypeRef = useRef<string | number | null>(null);
  const [fileVersionTargetId, setFileVersionTargetId] = useState<number | null>(
    null
  );
  const [uploadStatuses, setUploadStatuses] = useState<
    Record<string, UploadStatus>
  >({});

  const getTotalSize = () =>
    files.reduce((acc, file) => acc + (file.file?.size || 0), 0);
  const MAX_FILE_NAME_LENGTH = 100;
  const MAX_DESCRIPTION_LENGTH = 300;
  // const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 _.-]+$/;
  const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 _.,\-()]+$/;
  const ALPHANUMERIC_REGEXDESC =
    /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]+$/;

  const showError = (message: string) => {
    setSnackbarMessage(message);
    setError("error");
    setSnackbarOpen(true);
  };

  const validateFile = (file: File): boolean => {
    const errors: string[] = [];

    if (file.size === 0) {
      errors.push("The uploaded file is empty. Please select a valid file.");
    }

    if (file.type.startsWith("video/")) {
      errors.push("Video files are not allowed.");
    }

    if (!isAllowedFileType(file)) {
      errors.push(
        "Invalid file type. Please upload a PDF, Word document, image, spreadsheet, video, audio, or text file."
      );
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      errors.push(
        `The file you are trying to upload is too big. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`
      );
    }

    if (errors?.length > 0) {
      showError(errors.join("\n"));
      return false;
    }

    return true;
  };
  type ValidationResult = {
    isValid: boolean;
    errors: string[];
    totalFileSizeMB: number;
    totalFileCount: number;
  };
  const validateFileName_Description = (
    fileName: string,
    description?: string,
    existingFiles?: any,
    currentId?: string,
    isNewVersion: boolean = false
  ): boolean => {
    const errors: string[] = [];

    if (!ALPHANUMERIC_REGEX.test(fileName)) {
      errors.push(
        "File name contains invalid characters. Please rename the file and try again."
      );
    }

    if (fileName?.length > MAX_FILE_NAME_LENGTH) {
      errors.push(
        `File name is too long. Maximum allowed length is ${MAX_FILE_NAME_LENGTH} characters.`
      );
    }

    if (errors?.length > 0) {
      showError(errors.join("\n"));
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (
      prevDocumentTypeRef.current !== null &&
      documentType !== prevDocumentTypeRef.current
    ) {
      // Document type changed - reset all file fields
      setFiles([]);
      setNewFile({ files: [], description: "" }); // ✅ CORRECT
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Optionally reset file ids in parent
      onFileIdsUpdate?.([]);
    }

    // Update the ref with current value
    prevDocumentTypeRef.current = documentType;
  }, [documentType]);

  useEffect(() => {
    if (result?.addressesJson) {
      setFiles(
        result.addressesJson.map((file: any, index: any) => ({
          id: file.provisionalAttachmentID,
          fileName: file.fileName,
          description: file.description || "",
          file: file.cloudLocation,
          isEditing: false,
        }))
      );
    } else {
      setFiles([]);
    }
    if (result?.attachmentdetails) {
      const attachmentArray = JSON.parse(result.attachmentdetails);

      setFiles(
        attachmentArray.map((file: any, index: number) => ({
          id: file.provisionalattachmentid,
          fileName: file.Filename,
          description: file.Description,
          file: file.Cloudlocation ?? "",
          isEditing: false,
        }))
      );
    } else {
      setFiles([]);
    }
  }, [result]);

  useEffect(() => {
    if (isCancel) {
      // Reset the new file data

      setNewFile({ files: [], description: "" }); // ✅ CORRECT

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isCancel]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      e.target.value = "";

      const validFiles = selectedFiles.every((file) => validateFile(file));
      if (!validFiles) return;

      // Existing file names (case-insensitive, from both new and old)
      const existingFileNames = files
        .map((item) => item.fileName?.toLowerCase())
        .filter((name): name is string => !!name);

      // Generate renamed File objects with unique names
      const renamedFiles = selectedFiles.map((file) => {
        const uniqueFile = getRenamedFileIfDuplicate(file, existingFileNames);
        existingFileNames.push(uniqueFile.name.toLowerCase());
        return uniqueFile;
      });

      setIsSaveBtnDisableForRow(true);
      setNewFile({ ...newFile, files: renamedFiles });
    }
  };

  const handleEditFileChange = (
    id: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files?.length > 0) {
      const file = e.target.files[0];
      e.target.value = "";
      if (!validateFile(file)) return;
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, file, fileName: file.name } : item
        )
      );
    }
  };

  const updateFileIdsInParent = (updatedFiles: FileData[]) => {
    const fileIds = updatedFiles.map((file) => file.id).filter(Boolean);
    onFileIdsUpdate?.(fileIds, isSaveBtnDisableForRow);
  };

  React.useEffect(() => {
    updateFileIdsInParent(files);
  }, [files, isSaveBtnDisableForRow]);

  const CHUNK_SIZE = 8 * 1024 * 1024;

  const uploadFileInChunks = async (
    file: File,
    caseInfoId: string,
    description: string,
    additionalInfo: string,
    progress: (uploadedBytes: number) => void,
    cancelToken: CancelToken,
    onPageCountUpdate?: (fileId: string, pageCount: number) => void
  ): Promise<any> => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const abortControllers: AbortController[] = [];

    try {
      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        if (cancelToken.reason) throw cancelToken.reason;

        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append("File", chunk);
        formData.append("FileName", file.name);
        formData.append("ChunkNumber", (i + 1).toString());

        // Create new AbortController for each request
        const abortController = new AbortController();
        abortControllers.push(abortController);

        try {
          await uploadChunkRecord(formData, {
            cancelToken,
            signal: abortController.signal,
          });

          const percent = Math.round(((i + 1) / totalChunks) * 100);
          progress(percent);
        } finally {
          // Remove the controller after request completes
          abortControllers.pop();
        }
      }

      // Check cancellation before finalizing
      if (cancelToken.reason) throw cancelToken.reason;

      // Finalize upload
      const finalizePayload = {
        fileName: file.name,
        totalChunks: totalChunks,
        originalFileName: file.name,
        caseInfoId: Number(caseInfoId),
        description: description,
        additionalFileInfo: additionalInfo,
      };

      // return await uploadChunkRecordFinal(finalizePayload, cancelToken);
      const result = await uploadChunkRecordFinal(finalizePayload, cancelToken);
      if (result?.isSuccess && result?.result) {
        const provisionalId: any = result?.result;
        uploadPageCountAsync(file, provisionalId?.id, onPageCountUpdate);
        progress(100);
        return result;
      } else {
        throw new Error("Finalization failed - no ID returned");
      }
    } catch (error) {
      // Abort all ongoing requests
      abortControllers.forEach((controller) => controller.abort());

      if (axios.isCancel(error)) {
        console.error("Upload cancelled:", error.message);
        throw error;
      }
      throw error;
    }
  };

  const uploadPageCountAsync = async (
    file: File,
    provisionalAttachmentId: string,
    onPageCountUpdate?: (fileId: string, pageCount: number) => void
  ) => {
    try {
      const updateFormData = new FormData();
      updateFormData.append("File", file);
      updateFormData.append("ProvisionalAttachmentId", provisionalAttachmentId);
      const updatePageCount = await uploadPageCount(updateFormData);
      
      if (updatePageCount?.isSuccess && updatePageCount?.result?.pageCount && onPageCountUpdate) {
        onPageCountUpdate(provisionalAttachmentId, updatePageCount.result.pageCount);
      }
    } catch (error) {
      console.error("Error updating page count:", error);
    }
  };

  const handleAddFile = async () => {
    if (!newFile.files?.length) return;

    for (const file of newFile.files) {
      const isValid = validateFileName_Description(
        file.name,
        newFile.description,
        files
      );

      if (!isValid) {
        setSnackbarOpen(true);
        setError("error");
        return;
      }
    }
    // Prevent uploading same file(s) if already uploading
    const uploadingFileNames = Object.values(uploadStatuses)
      .filter((status) => status.isUploading)
      .flatMap((status) => status.fileName || []);

    const duplicateFiles = newFile.files.filter((file: any) =>
      uploadingFileNames.includes(file.name)
    );

    if (duplicateFiles?.length > 0) {
      const message =
        duplicateFiles?.length === 1
          ? `The file "${duplicateFiles[0].name}" is already being uploaded.`
          : `The files ${duplicateFiles
              .map((f: any) => `"${f.name}"`)
              .join(", ")} are already being uploaded.`;
      setSnackbarMessage(message);
      setError("error");
      setSnackbarOpen(true);
      return;
    }

    const uploadedFiles: FileData[] = [];

    // Size check (2 GB)
    const largeFile = newFile?.files?.find(
      (file: File) => file.size > FILE_SIZE.TWO_GB
    );
    if (largeFile) {
      setSnackbarMessage(
        `File "${largeFile.name}" exceeds the maximum allowed size of 2 GB.`
      );
      setError("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    // Store cancel tokens per file
    const fileCancelTokens: Record<number, any> = {};

    // Create upload status entries for all files before uploading starts
    const fileIds: number[] = [];
    newFile.files.forEach((file: any) => {
      const uniqueFileId = Date.now() + Math.random();
      fileIds.push(uniqueFileId);

      const fileCancelTokenSource = axios.CancelToken.source();
      fileCancelTokens[uniqueFileId] = fileCancelTokenSource;

      setUploadStatuses((prev) => ({
        ...prev,
        [uniqueFileId]: {
          fileName: file.name,
          progress: 0,
          isUploading: true,
          cancel: () => {
            fileCancelTokenSource.cancel("Upload cancelled by user");
          },
        },
      }));
    });

    try {
      // Sequential upload: one file at a time
      for (let i = 0; i < newFile.files.length; i++) {
        const fileObj = newFile.files[i];
        const uniqueFileId = fileIds[i];
        const fileCancelTokenSource = fileCancelTokens[uniqueFileId];

        try {
          const result = await uploadFileInChunks(
            fileObj,
            caseInfoId,
            newFile.description,
            comments,
            (progress: number) => {
              setUploadStatuses((prev) => ({
                ...prev,
                [uniqueFileId]: {
                  ...prev[uniqueFileId],
                  progress,
                },
              }));
            },
            fileCancelTokenSource.token,
            (fileId: string, pageCount: number) => {
              setFiles((prevFiles) => 
                prevFiles.map((file) => 
                  file.id === fileId 
                    ? { ...file, noofPages: pageCount }
                    : file
                )
              );
            }
          );

          if (result?.isSuccess && result.result?.id) {
            const res = result.result;
            const fileData: FileData = {
              fileName: fileObj.name,
              file: fileObj,
              description: newFile.description,
              id: res.id,
              isEditing: false,
              cloudStoragePath: res.cloudStoragePath,
            };
            setFiles((prevFiles) => [fileData, ...prevFiles]);
            uploadedFiles.push(fileData);
            onAdd?.(fileData);
          } else {
            console.error(`File "${fileObj.name}" upload failed.`);
          }
        } catch (error) {
          if (axios.isCancel(error)) {
            console.error(`Upload cancelled for ${fileObj.name}`);
          } else {
            console.error("Error adding file:", error);
          }
        } finally {
          // Remove this file from statuses after it finishes/cancels
          setUploadStatuses((prev) => {
            const newStatuses = { ...prev };
            delete newStatuses[uniqueFileId];
            return newStatuses;
          });
          delete fileCancelTokens[uniqueFileId];
        }
      }
    } catch (error) {
      console.error("Error in upload process:", error);
    } finally {
      if (uploadedFiles?.length > 0) {
        const message =
          uploadedFiles?.length === 1
            ? `File uploaded successfully.`
            : `Files uploaded successfully.`;
        setSnackbarMessage(message);
        setError("success");
        setSnackbarOpen(true);
        setIsSaveBtnDisable(true);
        setIsSaveBtnDisableForRow(false);
        dispatch(setIsUploadFile(false));
      }

      setNewFile({ files: [], description: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadProgress(0);
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setIsSaveBtnDisableForRow(true);
    const fileToEdit = files.find((file) => file.id === id);
    if (fileToEdit) {
      setOriginalData((prev) => ({
        ...prev,
        [id]: { ...fileToEdit, isEditing: true },
      }));
    }

    setFiles(
      files.map((item) =>
        item.id === id ? { ...item, isEditing: true } : item
      )
    );
  };

  const getRenamedFileIfDuplicate = (
    file: File,
    existingFileNames: string[]
  ): File => {
    let originalName = file.name;
    let newName = originalName;
    const parts = originalName.split(".");
    const ext = parts.length > 1 ? parts.pop()! : "";
    const base = parts.join(".");
    let counter = 1;

    // Rename if name already exists
    while (existingFileNames.includes(newName.toLowerCase())) {
      newName = `${base} (${counter}).${ext}`;
      counter++;
    }

    return new File([file], newName, { type: file.type });
  };

  const handleSaveEdit = async (id: string) => {
    const fileToUpdate = files.find((file) => file.id === id);
    const originalFile = originalData[id];
    if (!fileToUpdate) return;
    if (
      !validateFileName_Description(
        fileToUpdate.fileName,
        fileToUpdate.description,
        files,
        fileToUpdate.id
      )
    )
      return;
    const formData = new FormData();
    formData.append("ProvisionalAttachmentId", id);
    formData.append("CaseInfoId", caseInfoId);
    formData.append("Description", fileToUpdate.description);
    formData.append("AdditionalFileInfo", comments);

    const isFileChanged =
      fileToUpdate.file instanceof File &&
      fileToUpdate.file.name !== originalFile?.fileName;

    if (isFileChanged && fileToUpdate.file) {
      formData.append("file", fileToUpdate.file);
      formData.append("FileName", fileToUpdate.fileName);
    } else {
      formData.append("file", "");
      formData.append("FileName", "");
    }

    try {
      setLoading(true);
      setUploadProgress(0);
      const resultUpdate = await updateUploadRecordFiles(
        formData,
        (progress: number) => {
          setUploadProgress(progress);
        }
      );

      if (resultUpdate?.isSuccess) {
        setSnackbarMessage("File updated successfully");
        setError("success");
        setSnackbarOpen(true);
        setIsSaveBtnDisable(true);
        setIsSaveBtnDisableForRow(false);

        const updatedFile = {
          ...fileToUpdate,
          isEditing: false,
        };

        setFiles((prevFiles) =>
          prevFiles.map((file) => (file.id === id ? updatedFile : file))
        );

        setOriginalData((prev) => {
          const newData = { ...prev };
          delete newData[id];
          return newData;
        });

        onEdit?.(id, updatedFile);
      } else {
        showError("Failed to update the file record.");
      }
    } catch (error) {
      console.error("Error updating file:", error);
      showError("An error occurred while updating the file.");
    } finally {
      setUploadProgress(0);
      setLoading(false);
    }
  };

  const handleCancelEdit = (id: string) => {
    if (originalData[id]) {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...originalData[id], isEditing: false } : item
        )
      );
    } else {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, isEditing: false } : item
        )
      );
    }
  };

  const handleDelete = async (
    provisionalAttachmentId: number,
    fileName: string
  ) => {
    try {
      setLoadingNew(true);
      setShowDeleteModal(false);
      const resultUpdate = await deleteUploadRecordFile(
        provisionalAttachmentId,
        fileName
      );
      if (resultUpdate?.isSuccess) {
        setSnackbarMessage("File has been deleted");
        setError("success");
        setSnackbarOpen(true);
        setFileToDelete(null);
        setIsSaveBtnDisable(true);
      }
      const updatedFiles = files.filter(
        (file) => file.id !== provisionalAttachmentId
      );
      setFiles(updatedFiles);
      onDelete?.(provisionalAttachmentId.toString());
      updateFileIdsInParent(updatedFiles);
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setLoadingNew(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    const { name } = e.target;
    let value = e.target.value;
    if (value.startsWith(" ")) return;
    value = value.replace(/ {2,}/g, " ");
    if (value && !ALPHANUMERIC_REGEXDESC.test(value)) return;
    if (id) {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, [name]: value } : item
        )
      );
    } else {
      setNewFile({ ...newFile, [name]: value });
    }
  };

  const handleNewVersionInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    const { name } = e.target;
    let value = e.target.value;
    if (value.startsWith(" ")) return;
    value = value.replace(/\s{2,}/g, " ");
    if (value && !ALPHANUMERIC_REGEX.test(value)) return;
    setNewVersionFile({ ...newVersionFile, [name]: value });
  };

  const handleCancelNewFile = () => {
    // setNewFile({ id: "", fileName: "", description: "", isEditing: true });
    setNewFile({ files: [], description: "" }); // ✅ CORRECT
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelButton = (id?: string) => {
    if (id) {
      setFileToCancel(id);
      setIsSaveBtnDisableForRow(false);
    } else {
      setFileToCancel("new");
      setIsSaveBtnDisableForRow(false);
    }
    setShowModalNew(true);
  };

  const handleCloseModalNew = () => {
    setShowModalNew(false);
  };

  const handleConfirmCancelButton = () => {
    if (fileToCancel === "new") {
      handleCancelNewFile();
    } else {
      handleCancelEdit(fileToCancel);
    }
    setShowModalNew(false);
  };

  const handleDeleteFile = (id: number, fileName: string) => {
    setFileToDelete({ id, fileName });
    setShowDeleteModal(true);
  };

  const handleCloseModalNewVersion = () => {
    setShowNewVersionModal(false);
    setNewVersionFile({
      id: "",
      fileName: "",
      description: "",
      isEditing: true,
    });
    if (fileNewVersionInputRef.current) {
      fileNewVersionInputRef.current.value = "";
    }
  };

  const handleCloseModalDelete = () => {
    setShowDeleteModal(false);
  };

  const isAnyRowEditing = files.some((file) => file.isEditing);
  const handleDescriptionChange = (e: any) => {
    setNewFile((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  };

  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const viewPDF = (row: any) => {
    const fileUrl = row;

    const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");
    const isImage = /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
      fileUrl || ""
    );

    if (isPdf || isImage) {
      setSelectedPdfUrl(fileUrl); // Or better name: setSelectedFileUrl
      setIsViewerOpen(true);
    } else {
      alert("Unsupported file format");
    }
  };

  const handleCancelSingleUpload = (fileId: string) => {
    setUploadStatuses((prev) => {
      const newStatuses = { ...prev };
      const cancel = newStatuses[fileId]?.cancel;
      const fileName = newStatuses[fileId]?.fileName;
      if (cancel) cancel();
      if (fileName && newFile.files) {
        setNewFile((prev: any) => ({
          ...prev,
          files: prev.files.filter((f: any) => f.name !== fileName),
        }));

        // Clear the input if no files remain
        if (newFile.files?.length === 1 && fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
      delete newStatuses[fileId];
      const remainingUploads = Object.values(newStatuses).filter(
        (status) => status.isUploading
      );
      if (remainingUploads.length === 0) {
        dispatch(setIsUploadFile(false));
      }
      return newStatuses;
    });
  };
  return (
    <>
      <div
        style={{ position: "fixed", top: "20px", right: "20px", zIndex: 1050 }}
      >
        {Object.values(uploadStatuses).some((status) => status.isUploading) && (
          <UploadStatusFloatingIcon
            uploadStatuses={uploadStatuses}
            onCancelUpload={handleCancelSingleUpload}
            isUpload={true}
          />
        )}
      </div>
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />

      <div className="table-container picklist">
        <Table className="table  vieweditdatatable attachmentsection">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>File Name</th>
              <th style={{ width: "60%" }}>Description</th>
              <th style={{ width: "7%" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {/* {newFile.map((file, index) => ( */}
            <tr>
              <td style={{ width: "40%" }}>
                <Form.Group>
                  {/* Hidden File Input */}
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    style={{ display: "none" }}
                    disabled={isdisable}
                  />

                  {/* Custom Input Group with Text Field & Upload Button */}

                  <div
                    className="file-drop-zone"
                    onClick={() => {
                      if (!isAnyRowEditing) fileInputRef.current?.click();
                    }}
                    onMouseEnter={(e) => {
                      if (!isAnyRowEditing)
                        e.currentTarget.classList.add("drag-over");
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.classList.remove("drag-over");
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isAnyRowEditing)
                        e.currentTarget.classList.add("drag-over");
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove("drag-over");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove("drag-over");
                      if (!isAnyRowEditing) {
                        const files = Array.from(e.dataTransfer.files);
                        handleFileChange({ target: { files } } as any);
                      }
                    }}
                    style={{
                      cursor: isAnyRowEditing ? "not-allowed" : "pointer",
                    }}
                  >
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Choose a file or drag it here"
                        value={newFile.files.map((f) => f.name).join(", ")}
                        readOnly
                        className="border-end-0"
                        required={true}
                        disabled={isAnyRowEditing || isdisable}
                        style={{ cursor: "pointer", minHeight: "55px" }}
                      />

                      <Button
                        variant="outline-secondary"
                        className="p-0 border-start-0 attachbtn d-flex align-items-center"
                        // onClick={() => fileInputRef.current?.click()}
                        onClick={(e) => e.preventDefault()}
                        disabled={isAnyRowEditing || isdisable}
                      >
                        <span className="material-symbols-outlined color_brand mx-1">
                          attach_file
                        </span>
                      </Button>
                    </InputGroup>
                  </div>
                </Form.Group>
              </td>
              <td className="uploadrecordtext" style={{ width: "30%" }}>
                <Form.Control
                  as="textarea"
                  name="description"
                  style={{
                    padding: "5px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minHeight: "55px",
                    maxHeight: "120px",
                    fontSize: "13px",
                  }}
                  value={newFile.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  disabled={isAnyRowEditing || isdisable}
                  maxLength={300}
                />
              </td>
              <td style={{ width: "7%" }}>
                <div style={{ display: "flex", margin: "1px" }}>
                  <Button
                    className="btn btn-primary d-flex px-2 p-1 align-items-center justify-content-center"
                    onClick={handleAddFile}
                    disabled={isdisable}
                    //disabled={!newFile.fileName || isAnyRowEditing || view_Case}
                  >
                    <span className="material-symbols-outlined me-1">
                      add_circle
                    </span>{" "}
                    Add
                  </Button>
                  <Button
                    variant="danger"
                    className="border-0 bg-transparent mx-1 px-1"
                    onClick={() => handleCancelButton()}
                    disabled={isAnyRowEditing || isdisable}
                  >
                    <span className="material-icons color_brand mx-1">
                      close
                    </span>
                  </Button>
                </div>
              </td>
            </tr>
            {/* ))} */}
            {/* List all existing files */}
            {files.map((file) => (
              <tr key={file.id}>
                <td style={{ width: "40%" }}>
                  {file.fileName && file.fileName?.length > 15 ? (
                    <OverlayTrigger
                      placement="auto"
                      delay={{ show: 200, hide: 10 }}
                      overlay={
                        <Tooltip id={`tooltip-fileName-${file.id}`}>
                          {file.fileName}
                        </Tooltip>
                      }
                    >
                      <span
                        className="d-inline-block text-truncate"
                        style={{
                          maxWidth: "250px",
                          cursor: "default",
                        }}
                      >
                        {file.fileName}
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <span style={{ cursor: "default" }}>{file.fileName}</span>
                  )}
                </td>
                {/* <td style={{ width: "33%" }}>
                  {file.isEditing ? (
                    <Form.Group>
                      <Form.Control
                        type="file"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleEditFileChange(file.id, e)
                        }
                        ref={(el: any) =>
                          (editFileInputRefs.current[file.id] = el)
                        }
                        style={{ display: "none", cursor: "pointer" }}
                      />
                      <div
                        onClick={() => {
                          editFileInputRefs.current[file.id]?.click();
                        }}
                         onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add("drag-over");
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove("drag-over");
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove("drag-over");
                          const files = Array.from(e.dataTransfer.files);
                          const firstFile = files.length ? [files[0]] : [];
                          handleEditFileChange(file.id, { target: { files: firstFile } } as any);
                        }}
                        style={{
                          cursor: isAnyRowEditing ? "pointer" : "pointer",
                        }}
                      >
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Choose a file..."
                            value={file.fileName}
                            readOnly
                            className="border-end-0"
                            required
                            style={{ cursor: "pointer", minHeight: "55px" }}
                            disabled={view_Case}
                          />
                          <Button
                            variant="outline-secondary"
                            className="p-0 border-start-0 attachbtn d-flex align-items-center"
                            // onClick={() =>
                            //   editFileInputRefs.current[file.id]?.click()
                            // }
                            onClick={(e) => e.preventDefault()}
                            disabled={view_Case}
                          >
                            <span className="material-symbols-outlined color_brand mx-1">
                              attach_file
                            </span>
                          </Button>
                        </InputGroup>
                      </div>
                    </Form.Group>
                  ) : (
                    file.fileName
                  )}
                </td> */}
                <td className="uploadrecordtext" style={{ width: "30%" }}>
                  {file.isEditing ? (
                    <Form.Control
                      as="textarea"
                      name="description"
                      style={{
                        padding: "5px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        minHeight: "55px",
                        maxHeight: "120px",
                        fontSize: "13px",
                      }}
                      value={file.description}
                      onChange={(e) => handleInputChange(e, file.id)}
                      //disabled={view_Case}
                    />
                  ) : (
                    file.description
                  )}
                </td>
                <td style={{ width: "7%" }}>
                  <div
                    style={{
                      display: "flex",
                      margin: "1px",
                      justifyContent: "Center",
                    }}
                  >
                    {file.isEditing ? (
                      <>
                        <Button
                          variant="success"
                          className="border-0 bg-transparent"
                          onClick={() => handleSaveEdit(file.id)}
                          //disabled={view_Case}
                        >
                          <span className="material-icons color_brand mx-0">
                            save
                          </span>
                        </Button>
                        <Button
                          variant="danger"
                          className="border-0 bg-transparent"
                          onClick={() => handleCancelButton(file.id)}
                          // disabled={view_Case}
                        >
                          <span className="material-icons color_brand mx-0">
                            close
                          </span>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="success"
                          className="border-0 bg-transparent m-0 px-0"
                          onClick={() => handleEdit(file.id)}
                          disabled={isAnyRowEditing || isdisable}
                        >
                          <span className="material-icons color_brand m-0">
                            {" "}
                            edit
                          </span>
                        </Button>
                        <Button
                          variant="danger"
                          className="border-0 bg-transparent m-0"
                          // onClick={() => handleDelete(file.id, file.fileName)}
                          onClick={() =>
                            handleDeleteFile(file.id, file.fileName)
                          }
                          disabled={isAnyRowEditing || isdisable}
                        >
                          <span className="material-icons material-symbols-outlined text-danger color_brand m-0">
                            delete
                          </span>
                        </Button>
                        {file?.fileName &&
                          /\.(pdf|png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                            file.fileName
                          ) && (
                            <Button
                              variant="success"
                              className="border-0 bg-transparent m-1 p-0"
                              style={{ cursor: "pointer" }}
                              onClick={() => viewPDF?.(file.cloudStoragePath)}
                            >
                              <span className="material-icons material-symbols-outlined color_brand mt-1 mx-1">
                                document_search
                              </span>
                            </Button>
                          )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={showModalNew} onHide={handleCloseModalNew}>
        <Modal.Body className="fw-bold text-left modelheading ">
          <div>Are you sure you want to cancel?</div>
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={handleCloseModalNew}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={handleConfirmCancelButton}
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteModal} onHide={handleCloseModalDelete}>
        <Modal.Body className="fw-bold text-left modelheading ">
          <div>Are you sure you want to delete this record?</div>
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={handleCloseModalDelete}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={() =>
              fileToDelete &&
              handleDelete(fileToDelete.id, fileToDelete.fileName)
            }
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <PdfViewerDrawer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        fileUrl={selectedPdfUrl}
      />
    </>
  );
};

export default EditableFileTable;
